import React, { useContext, useEffect, useState } from 'react';
import { getRole } from '../../api/user';
import { AuthContext } from '../../contexts/AuthProvider';
import { motion } from 'framer-motion';
import { FiRefreshCw } from 'react-icons/fi';

const Welcome = () => {
  const { user } = useContext(AuthContext);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    getRole(user?.email).then(data => {
      setRole(data);
      setLoading(false);
    });
  }, [user]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className='relative h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center items-center px-4 overflow-hidden'
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Refresh button - fixed position with z-index to ensure it's on top */}
      <motion.button
        onClick={handleRefresh}
        className="fixed top-6 right-6 p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all z-50"
        whileHover={{ rotate: 90, scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        variants={itemVariants}
        aria-label="Refresh page"
      >
        <FiRefreshCw className={`text-indigo-600 ${isRefreshing ? 'animate-spin' : ''}`} size={24} />
      </motion.button>

      {/* Main content */}
      <div className="relative z-10 max-w-2xl w-full bg-white rounded-xl shadow-lg p-8 text-center mx-4">
        <motion.div variants={itemVariants}>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Welcome{user?.displayName ? `, ${user.displayName}` : ''}!
          </h1>
        </motion.div>

        <motion.div variants={itemVariants}>
          <p className="text-lg text-gray-600 mb-8">
            You're logged in as <span className="font-medium text-indigo-600">{user?.email}</span>
          </p>
        </motion.div>

        <motion.div 
          className="mt-6"
          variants={itemVariants}
        >
          {!loading && role ? (
            <div className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-full shadow-md">
              <motion.p 
                className="text-xl font-medium"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {role === 'admin' ? 'Admin Dashboard' : 'User Dashboard'}
              </motion.p>
            </div>
          ) : (
            <div className="h-10 flex items-center justify-center">
              <div className="animate-pulse text-gray-400">Loading your dashboard...</div>
            </div>
          )}
        </motion.div>

        <motion.div 
          className="mt-12"
          variants={itemVariants}
        >
          <p className="text-gray-500">
            Get started by exploring the navigation menu
          </p>
        </motion.div>
      </div>

      {/* Animated background elements */}
      <motion.div 
        className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-200 rounded-full opacity-20 -mb-16 -ml-16"
        animate={{
          y: [0, -20, 0],
          x: [0, 10, 0]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute top-0 right-0 w-48 h-48 bg-blue-200 rounded-full opacity-20 -mt-16 -mr-16"
        animate={{
          y: [0, 20, 0],
          x: [0, -10, 0]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />
    </motion.div>
  );
};

export default Welcome;